import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Plus,
  Search,
  MoreHorizontal,
  Warehouse as WarehouseIcon,
  Loader2,
  Pencil,
  Trash2,
  MapPin,
  Star,
  Package,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { WarehouseModal } from '@/components/trading/warehouse-modal';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { usePermission } from '@/components/guards/permission-guard';

interface Warehouse {
  id: string;
  code: string;
  name: string;
  type: string;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  isActive: boolean;
  isDefault: boolean;
  _count?: {
    stockLevels: number;
  };
}

const warehouseTypeColors: Record<string, string> = {
  MAIN: 'bg-blue-100 text-blue-700',
  TRANSIT: 'bg-amber-100 text-amber-700',
  CONSIGNMENT: 'bg-purple-100 text-purple-700',
  RETURNS: 'bg-red-100 text-red-700',
};

export function WarehousesPage() {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');
  const canCreate = usePermission('WAREHOUSES', 'CREATE');
  const canEdit = usePermission('WAREHOUSES', 'EDIT');
  const canDelete = usePermission('WAREHOUSES', 'DELETE');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState<Warehouse | undefined>();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState<string | null>(null);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setTimeout(() => {
      setDebouncedSearch(value);
    }, 300);
  };

  const { data, isLoading, error } = trpc.warehouse.list.useQuery({});

  const utils = trpc.useUtils();

  const deleteMutation = trpc.warehouse.delete.useMutation({
    onSuccess: () => {
      utils.warehouse.list.invalidate();
      showSuccessToast(t('common.deleted'));
      setDeleteConfirmOpen(false);
      setWarehouseToDelete(null);
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const setDefaultMutation = trpc.warehouse.setDefault.useMutation({
    onSuccess: () => {
      utils.warehouse.list.invalidate();
      showSuccessToast(t('common.updated'));
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const handleAddWarehouse = () => {
    setEditingWarehouse(undefined);
    setIsModalOpen(true);
  };

  const handleEditWarehouse = (warehouse: Warehouse) => {
    setEditingWarehouse(warehouse);
    setIsModalOpen(true);
  };

  const handleDeleteWarehouse = (id: string) => {
    setWarehouseToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleSetDefault = (id: string) => {
    setDefaultMutation.mutate(id);
  };

  const confirmDelete = () => {
    if (warehouseToDelete) {
      deleteMutation.mutate(warehouseToDelete);
    }
  };

  const warehouses = data ?? [];

  const getWarehouseTypeLabel = (type: string) => {
    switch (type) {
      case 'MAIN':
        return t('warehouses.main');
      case 'TRANSIT':
        return t('warehouses.transit');
      case 'CONSIGNMENT':
        return t('warehouses.consignment');
      case 'RETURNS':
        return t('warehouses.returns');
      default:
        return type;
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('warehouses.title')}</h1>
          <p className="text-muted-foreground">
            {t('warehouses.subtitle')}
            {warehouses.length > 0 && ` (${warehouses.length} ${tCommon('total')})`}
          </p>
        </div>
        {canCreate && (
          <Button onClick={handleAddWarehouse}>
            <Plus className="mr-2 h-4 w-4" />
            {t('warehouses.addWarehouse')}
          </Button>
        )}
      </div>

      {/* Warehouse Modal */}
      <WarehouseModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        warehouse={editingWarehouse}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title={tCommon('delete')}
        description={t('warehouses.deleteConfirm')}
        confirmLabel={tCommon('delete')}
        variant="destructive"
        onConfirm={confirmDelete}
        isLoading={deleteMutation.isPending}
      />

      {/* Search */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder={t('warehouses.searchPlaceholder')}
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 w-full border border-border bg-background pl-10 pr-4 text-sm focus:outline-none focus:border-foreground focus:ring-1 focus:ring-foreground/20"
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-muted-foreground">{t('common.loading')}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="border border-red-200 bg-red-50 p-4 text-red-700">
          <p className="font-medium">{t('common.error')}</p>
          <p className="text-sm">{error.message}</p>
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && warehouses.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <WarehouseIcon className="h-12 w-12 text-muted-foreground/50" />
          <h3 className="mt-4 text-lg font-medium">{t('warehouses.noWarehouses')}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {debouncedSearch ? t('warehouses.noWarehouses') : t('warehouses.noWarehousesDesc')}
          </p>
          {!debouncedSearch && canCreate && (
            <Button className="mt-4" onClick={handleAddWarehouse}>
              <Plus className="mr-2 h-4 w-4" />
              {t('warehouses.addWarehouse')}
            </Button>
          )}
        </div>
      )}

      {/* Warehouses Grid */}
      {!isLoading && !error && warehouses.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {warehouses.map((warehouse) => (
            <div
              key={warehouse.id}
              className="group border border-border bg-card p-5 transition-shadow hover:shadow-md"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center bg-cyan-100">
                    <WarehouseIcon className="h-6 w-6 text-cyan-600" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold">{warehouse.name}</h3>
                      {warehouse.isDefault && (
                        <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {t('warehouses.code')}: {warehouse.code}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {canEdit && (
                      <DropdownMenuItem onClick={() => handleEditWarehouse(warehouse)}>
                        <Pencil className="mr-2 h-4 w-4" />
                        {tCommon('edit')}
                      </DropdownMenuItem>
                    )}
                    {!warehouse.isDefault && (
                      <DropdownMenuItem onClick={() => handleSetDefault(warehouse.id)}>
                        <Star className="mr-2 h-4 w-4" />
                        {t('warehouses.setDefault')}
                      </DropdownMenuItem>
                    )}
                    {canDelete && (
                      <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteWarehouse(warehouse.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {tCommon('delete')}
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <span
                  className={`px-2 py-0.5 text-xs font-medium ${
                    warehouseTypeColors[warehouse.type] || 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {getWarehouseTypeLabel(warehouse.type)}
                </span>
                <span
                  className={`px-2 py-0.5 text-xs font-medium ${
                    warehouse.isActive
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {warehouse.isActive ? t('warehouses.isActive') : tCommon('inactive')}
                </span>
              </div>

              {(warehouse.address || warehouse.city || warehouse.country) && (
                <div className="mt-4 flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                  <span>
                    {[warehouse.address, warehouse.city, warehouse.country]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>
              )}

              <div className="mt-4 flex items-center justify-between border-t pt-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Package className="h-4 w-4" />
                  <span>{warehouse._count?.stockLevels || 0} {t('warehouses.productCount')}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
